import type { EventDataChanged } from '../../../domain/message';
import { CompanyTabName } from '../../../domain/page';
import { AUTO_OPEN_LINKEDIN_KEY } from '../../../domain/settings';
import type { Commands } from '../../../messageHandler';
import DataExtractionService from '../../dataExtraction/DataExtractionService';
import ScrappingMonitoringService from '../../scrapingMonitoring/scrappingMonitoringService';
import UrlService from '../../UrlService';

const handleAutoOpen = (commands: Commands, url: URL | undefined): void => {
  const isAutoOpenEnabled = commands.getSettingsStorageValue(AUTO_OPEN_LINKEDIN_KEY);

  if (UrlService.isAutoOpenLinkedinPage(url) && isAutoOpenEnabled) {
    window.parent.postMessage({ message: 'open' }, '*');
  }
};

const handleIframeLoad = (commands: Commands, url: URL | undefined): void => {
  if (UrlService.isLinkedinCompanyPage(url)) {
    const companyLinkedinId = UrlService.getCompanyLinkedinIdFromUrl(url);
    if (companyLinkedinId) {
      commands.setCompanyPage({ companyLinkedinId });
    }
    if (UrlService.isLinkedinCompanyPeoplePage(url)) {
      commands.setCompanyTabName(CompanyTabName.Contacts);
    }
    return;
  }

  if (UrlService.isLinkedinContactPage(url)) {
    const linkedinId = UrlService.getContactLinkedinIdFromUrl(url);
    if (linkedinId) {
      commands.setContactPage({ linkedinId });
    }
    return;
  }

  if (
    UrlService.isLinkedinSearchPage(url) ||
    UrlService.isSalesNavSearchPage(url) ||
    UrlService.isLinkedinSearchAllPage(url) ||
    UrlService.isSalesNavListPage(url)
  ) {
    commands.setSearchPage();
    return;
  }

  if (UrlService.isSalesNavContactPage(url)) {
    const linkedinSalesNavId = DataExtractionService.salesNav.extractSalesNavLinkedinId(url?.pathname || '');
    commands.setContactPage({ linkedinSalesNavId });
    return;
  }

  if (UrlService.isSalesNavCompanyPage(url)) {
    const companyLinkedinId = UrlService.getCompanyLinkedinIdFromSalesNavPathname((url as URL).pathname);
    if (companyLinkedinId) {
      commands.setCompanyPage({ companyLinkedinId });
    }
    return;
  }

  commands.setHomePage();
};

const handleDataChange = (commands: Commands, event: EventDataChanged, url: URL | undefined): void => {
  if (UrlService.isLinkedinCompanyPage(url)) {
    const companyDomain = DataExtractionService.linkedin.extractCompanyDomain(event.data);
    if (companyDomain) {
      commands.setCompanyDomain(companyDomain);
    }
    return;
  }

  if (UrlService.isLinkedinContactPage(url)) {
    const contactInfo = DataExtractionService.linkedin.extractContactInfo(event.data);
    commands.setCompanyLinkedinId(contactInfo.numericCompanyLinkedinIds?.[0]?.toString());
    commands.setCollectedContactInfo(contactInfo);
    if (
      commands.getState().isOpen &&
      event.data.length > 0 &&
      event.data.some(({ name }) => name === 'Experience for scrapping monitor')
    ) {
      ScrappingMonitoringService.dispatchScrappingEvent({ data: event.data, url });
    }
    return;
  }

  if (UrlService.isLinkedinSearchPage(url)) {
    const searchInfo = DataExtractionService.linkedin.extractContactSearchInfo(event.data);
    commands.setCollectedContactsSearchInfo(searchInfo);
    if (event.data?.length > 0 && commands.getState().isOpen) {
      ScrappingMonitoringService.dispatchScrappingEvent({ data: searchInfo, url });
    }
    return;
  }

  if (UrlService.isLinkedinSearchAllPage(url)) {
    const searchAllInfo = DataExtractionService.linkedin.extractAllSearchInfo(event.data);
    commands.setCollectedContactsSearchInfo(searchAllInfo);
    if (event.data?.length > 0 && commands.getState().isOpen) {
      ScrappingMonitoringService.dispatchScrappingEvent({ data: searchAllInfo, url });
    }
    return;
  }

  if (UrlService.isSalesNavContactPage(url)) {
    const contactInfo = DataExtractionService.salesNav.extractContactInfo(event.data);
    commands.setCollectedContactInfo(contactInfo);
    commands.setCompanyLinkedinId(contactInfo.numericCompanyLinkedinIds?.[0]?.toString());
    return;
  }

  if (UrlService.isSalesNavCompanyPage(url)) {
    const companyDomain = DataExtractionService.linkedin.extractCompanyDomain(event.data);
    if (companyDomain) {
      commands.setCompanyDomain(companyDomain);
    }
    return;
  }

  if (UrlService.isSalesNavSearchPage(url)) {
    const page = UrlService.getPageFromUrl(url);
    const total = DataExtractionService.salesNav.extractResultsCount(event.data);
    const pageSize = 25;
    commands.setSearchPagePagination({ page, total, pageSize });

    const searchInfo = DataExtractionService.salesNav.extractContactSearchInfo(event.data);
    commands.setCollectedContactsSearchInfo(searchInfo);
    return;
  }

  if (UrlService.isSalesNavListPage(url)) {
    const searchInfo = DataExtractionService.salesNav.extractContactSearchInfo(event.data);
    commands.setCollectedContactsSearchInfo(searchInfo);
    return;
  }
};

export const linkedinMessageHandler = {
  handleIframeLoad,
  handleDataChange,
  handleAutoOpen,
};
